import { supabase } from './supabase';
import { callEdgeFunction } from './edgeFunctions';
import { renderTemplateBody } from './whatsappTemplates';
import { captureError } from './sentry';
import { normalizePhone } from '../shared/phone';
import { formatLeadAddress } from '../types/crm';

export interface WhatsAppConversation {
  id: string;
  customer_phone: string;
  contact_id: string | null;
  lead_id: string | null;
  project_id: string | null;
  assigned_user_id: string | null;
  last_message_at: string | null;
  last_customer_message_at: string | null;
  customer_name: string | null;
  status: 'active' | 'closed' | 'unassigned';
  created_at: string;
  contacts?: { name: string; company: string | null } | null;
  leads?: { contact_name: string; address: string | null; plaats: string | null } | null;
  projects?: { name: string; project_number: string } | null;
  assigned_user?: { full_name: string } | null;
}

export interface WhatsAppMessage {
  id: string;
  conversation_id: string;
  wamid: string | null;
  direction: 'outbound' | 'inbound';
  body: string | null;
  media_urls: string[] | null;
  status: string;
  template_name: string | null;
  sent_by: string | null;
  error_message: string | null;
  created_at: string;
  sender?: { full_name: string } | null;
}

export interface SendWhatsAppParams {
  to: string;
  body?: string;
  template_name?: string;
  template_language?: string;
  template_components?: unknown[];
  media_url?: string;
  media_urls?: string[];
  conversation_id?: string;
  project_id?: string;
  lead_id?: string;
  contact_id?: string;
}

export interface SendWhatsAppResponse {
  success: boolean;
  message_id?: string;
  conversation_id?: string;
  error?: string;
}

export async function sendWhatsApp(params: SendWhatsAppParams): Promise<SendWhatsAppResponse> {
  try {
    return (await callEdgeFunction(
      'send-whatsapp',
      params as unknown as Record<string, unknown>,
    )) as unknown as SendWhatsAppResponse;
  } catch (error) {
    captureError(error instanceof Error ? error : new Error(String(error)), {
      action: 'send-whatsapp',
    });
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Fout bij verzenden WhatsApp bericht',
    };
  }
}

export async function sendWhatsAppTemplate(
  to: string,
  templateName: string,
  language = 'nl',
  components?: unknown[],
  context?: {
    conversation_id?: string;
    project_id?: string;
    lead_id?: string;
    contact_id?: string;
  },
): Promise<SendWhatsAppResponse> {
  // Ingevulde tekst meesturen zodat het bericht leesbaar wordt opgeslagen in
  // de eigen inbox; de edge function verstuurt bij een template_name altijd
  // de template zelf (body wordt alleen gebruikt voor opslag).
  const body = renderTemplateBody(templateName, components) ?? undefined;
  return sendWhatsApp({
    to,
    body,
    template_name: templateName,
    template_language: language,
    template_components: components,
    ...context,
  });
}

export async function checkWhatsAppConfiguration(): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('whatsapp_settings')
      .select('is_configured')
      .maybeSingle();

    if (error) {
      captureError(error instanceof Error ? error : new Error(String(error)), {
        action: 'check-whatsapp-configuration',
      });
      return false;
    }

    return data?.is_configured || false;
  } catch (error) {
    captureError(error instanceof Error ? error : new Error(String(error)), {
      action: 'check-whatsapp-configuration',
    });
    return false;
  }
}

export async function fetchConversations(filters?: {
  status?: string;
  assigned_user_id?: string;
  project_id?: string;
  lead_id?: string;
  phone?: string;
}): Promise<WhatsAppConversation[]> {
  let query = supabase
    .from('whatsapp_conversations')
    .select(
      `
      *,
      contacts:contact_id (name, company),
      leads:lead_id (contact_name, address, plaats),
      projects:project_id (name, project_number),
      assigned_user:assigned_user_id (full_name)
    `,
    )
    .order('last_message_at', { ascending: false, nullsFirst: false });

  if (filters?.status) query = query.eq('status', filters.status);
  if (filters?.assigned_user_id) query = query.eq('assigned_user_id', filters.assigned_user_id);

  // Match op de opgeslagen koppeling (project_id/lead_id) OF op het
  // genormaliseerde telefoonnummer, zodat een gesprek ook verschijnt als het
  // (nog) niet expliciet aan het project/de lead gekoppeld is.
  const orConds: string[] = [];
  if (filters?.project_id) orConds.push(`project_id.eq.${filters.project_id}`);
  if (filters?.lead_id) orConds.push(`lead_id.eq.${filters.lead_id}`);
  const normalizedPhone = normalizePhone(filters?.phone);
  if (normalizedPhone) orConds.push(`customer_phone.eq.${normalizedPhone}`);
  if (orConds.length > 0) query = query.or(orConds.join(','));

  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as WhatsAppConversation[];
}

export interface ConversationPreview {
  body: string | null;
  direction: 'outbound' | 'inbound';
  template_name: string | null;
  has_media: boolean;
  created_at: string;
  /** Aantal inkomende berichten sinds ons laatste antwoord. */
  unreadCount: number;
}

/**
 * Laatste bericht + ongelezen-teller per gesprek, voor de inboxlijst.
 * "Ongelezen" = inkomende berichten die na ons laatste uitgaande bericht
 * zijn binnengekomen (er is geen echte gelezen-status per gebruiker).
 */
export async function fetchConversationPreviews(
  conversationIds: string[],
): Promise<Record<string, ConversationPreview>> {
  const previews: Record<string, ConversationPreview> = {};
  if (conversationIds.length === 0) return previews;

  const { data, error } = await supabase
    .from('whatsapp_messages')
    .select('conversation_id, body, direction, template_name, media_urls, created_at')
    .in('conversation_id', conversationIds)
    .order('created_at', { ascending: false })
    .limit(Math.min(conversationIds.length * 25, 2000));
  if (error) throw error;

  // Berichten staan nieuwste-eerst: tel inbound tot het eerste outbound bericht.
  const geteld = new Set<string>();
  for (const m of data || []) {
    const id = m.conversation_id as string;
    if (!previews[id]) {
      previews[id] = {
        body: m.body,
        direction: m.direction as 'outbound' | 'inbound',
        template_name: m.template_name,
        has_media: Array.isArray(m.media_urls) && m.media_urls.length > 0,
        created_at: m.created_at,
        unreadCount: 0,
      };
    }
    if (!geteld.has(id)) {
      if (m.direction === 'inbound') {
        previews[id].unreadCount += 1;
      } else {
        geteld.add(id);
      }
    }
  }
  return previews;
}

export async function fetchMessages(
  conversationId: string,
  limit = 50,
  before?: string,
): Promise<WhatsAppMessage[]> {
  let query = supabase
    .from('whatsapp_messages')
    .select(
      `
      *,
      sender:sent_by (full_name)
    `,
    )
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (before) {
    query = query.lt('created_at', before);
  }

  const { data, error } = await query;
  if (error) throw error;
  return ((data || []) as WhatsAppMessage[]).reverse();
}

export async function assignConversation(
  conversationId: string,
  userId: string | null,
): Promise<void> {
  const { error } = await supabase
    .from('whatsapp_conversations')
    .update({
      assigned_user_id: userId,
      status: userId ? 'active' : 'unassigned',
    })
    .eq('id', conversationId);

  if (error) throw error;
}

export async function linkConversation(
  conversationId: string,
  link: { project_id?: string; lead_id?: string; contact_id?: string },
): Promise<void> {
  const { error } = await supabase
    .from('whatsapp_conversations')
    .update(link)
    .eq('id', conversationId);

  if (error) throw error;
}

export async function archiveConversation(conversationId: string): Promise<void> {
  const { error } = await supabase
    .from('whatsapp_conversations')
    .update({ status: 'closed' })
    .eq('id', conversationId);

  if (error) throw error;
}

export function isWithin24HourWindow(lastCustomerMessageAt: string | null): boolean {
  if (!lastCustomerMessageAt) return false;
  const windowEnd = new Date(lastCustomerMessageAt).getTime() + 24 * 60 * 60 * 1000;
  return Date.now() < windowEnd;
}

export function getWindowTimeRemaining(lastCustomerMessageAt: string | null): string | null {
  if (!lastCustomerMessageAt) return null;
  const windowEnd = new Date(lastCustomerMessageAt).getTime() + 24 * 60 * 60 * 1000;
  const remaining = windowEnd - Date.now();
  if (remaining <= 0) return null;

  const hours = Math.floor(remaining / (60 * 60 * 1000));
  const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
  return `${hours}u ${minutes}m`;
}

/** Aantal open WhatsApp-gesprekken dat aandacht vraagt (ongelezen of onbekend). */
export async function fetchWhatsAppActionCount(): Promise<number> {
  const { data, error } = await supabase
    .from('whatsapp_conversations')
    .select('id, status, last_customer_message_at, last_message_at')
    .neq('status', 'closed')
    .limit(500);

  if (error) throw error;

  return (data || []).filter(
    (c) =>
      c.status === 'unassigned' ||
      (c.last_customer_message_at &&
        (!c.last_message_at || c.last_customer_message_at >= c.last_message_at)),
  ).length;
}

/** Primaire titel in de WhatsApp-inbox: projectadres, lead-adres, of contactnaam. */
export function getWhatsAppConversationTitle(conv: WhatsAppConversation): string {
  if (conv.projects?.name) return conv.projects.name;
  if (conv.leads) {
    const adres = formatLeadAddress(conv.leads.address, conv.leads.plaats);
    if (adres) return adres;
    if (conv.leads.contact_name) return conv.leads.contact_name;
  }
  if (conv.contacts?.name) return conv.contacts.name;
  if (conv.customer_name) return conv.customer_name;
  return conv.customer_phone;
}

/** Ondertitel: contactnaam of telefoonnummer. */
export function getWhatsAppConversationSubtitle(conv: WhatsAppConversation): string {
  const parts: string[] = [];
  if (conv.leads?.contact_name) parts.push(conv.leads.contact_name);
  if (conv.projects) parts.push(`${conv.projects.project_number} - ${conv.projects.name}`);
  else if (conv.contacts?.company) parts.push(conv.contacts.company);
  return parts.join(' · ') || conv.customer_phone;
}
